import { usePopover } from '@/hooks/usePopover';
import { SwapVert, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Menu, MenuItem, Tooltip, ListItemText, useTheme } from '@mui/material';
import { GridSortModel, gridSortModelSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid-pro';
import { useTransition } from 'react';

const SORT_OPTIONS = [
  { field: 'name', label: 'Name' },
  { field: 'createdAt', label: 'Created' },
  { field: 'creator', label: 'Created By' },
] as const;

export const SortButton = ({ onSortModelChange }: { onSortModelChange: (model: GridSortModel) => void }) => {
  const apiReference = useGridApiContext();
  const theme = useTheme();
  const sortModel = useGridSelector(apiReference, gridSortModelSelector);
  const { open, close, isOpen, anchorElement } = usePopover();
  const [isPending, startTransition] = useTransition();

  const activeSort = sortModel[0];
  const hasActiveSort = !!activeSort;

  const handleSortSelect = (field: string) => {
    startTransition(() => {
      if (activeSort?.field === field) {
        if (activeSort.sort === 'asc') {
          onSortModelChange([{ field, sort: 'desc' }]);
        } else {
          onSortModelChange([]);
        }
      } else {
        onSortModelChange([{ field, sort: 'asc' }]);
      }
    });
    close();
  };

  const SortDirectionIcon = activeSort?.sort === 'asc' ? ArrowUpward : ArrowDownward;

  return (
    <>
      <Tooltip title='Sort' placement='bottom'>
        <Box
          sx={{
            position: 'relative',
            width: 28,
            height: 28,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            size='small'
            onClick={open}
            disabled={isPending}
            sx={{
              color: hasActiveSort ? theme.palette.primary.main : theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              '&.Mui-disabled': { color: theme.palette.text.secondary },
            }}
          >
            <SwapVert fontSize='small' />
          </IconButton>
          {isPending && (
            <CircularProgress
              size={26}
              thickness={2}
              sx={{
                position: 'absolute',
                top: 1,
                left: 1,
                color: theme.palette.primary.main,
                pointerEvents: 'none',
              }}
            />
          )}
        </Box>
      </Tooltip>
      <Menu anchorEl={anchorElement} open={isOpen} onClose={close} slotProps={{ paper: { sx: { minWidth: 160 } } }}>
        {SORT_OPTIONS.map(option => (
          <MenuItem key={option.field} onClick={() => handleSortSelect(option.field)}>
            <ListItemText>{option.label}</ListItemText>
            {activeSort?.field === option.field && <SortDirectionIcon fontSize='small' sx={{ ml: 1 }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
